import { action, atom, withAssign } from '@reatom/framework'
import { withLocalStorage } from '@reatom/persist-web-storage'
import { currentMonitor } from '@tauri-apps/api/window'

import { Result } from '@blade/std-lib'
import { STORAGE_PREFIX } from '@root/shared/constants'

import { PlatformUtil } from '../platform-util'

import { isTauriAtom } from './platform-atom'

type ScaleFactorSource = 'user' | 'automatic'
type ScaleFactorValue = {
  source: ScaleFactorSource
  value: number
}

const scaleFactorAtom_ = atom<ScaleFactorValue | undefined>(
  undefined,
  'scaleFactorAtom_',
).pipe(
  withLocalStorage({
    key: `${STORAGE_PREFIX}/v1/scaleFactor`,
  }),
)

export const PREDEFINED_SCALE_FACTORS = [0.8, 0.9, 1, 1.1, 1.2]

export const setScaleFactorAction = action(
  async (ctx, scaleFactor, source: ScaleFactorSource) => {
    const result = await ctx.schedule(() =>
      PlatformUtil.setScaleFactor(scaleFactor),
    )

    const appliedScaleFactor = Result.match(result, {
      onFailure: ([_, fallback]) => fallback,
      onSuccess: (x) => x,
    })

    scaleFactorAtom_(ctx, {
      source,
      value: appliedScaleFactor,
    })

    return result
  },
  'setScaleFactorAction',
)

export const scaleFactorAtom = scaleFactorAtom_.pipe(
  withAssign(() => ({
    setScaleFactorAction,
  })),
)

const BASELINE_WIDTH = 1740
export const initializeScaleFactorAction = action(async (ctx) => {
  const scaleFactor = ctx.get(scaleFactorAtom)
  if (scaleFactor) {
    PlatformUtil.setScaleFactor(scaleFactor.value)
    return
  }

  if (!ctx.get(isTauriAtom)) {
    return
  }
  const monitor = await ctx.schedule(() => currentMonitor())
  if (!monitor) {
    return
  }

  const logicalWidth = monitor.size.width / monitor.scaleFactor
  const idealScaleFactor = logicalWidth / BASELINE_WIDTH

  const closestScaleFactor = findClosest(
    PREDEFINED_SCALE_FACTORS,
    idealScaleFactor,
  )
  if (closestScaleFactor <= 1) {
    return
  }

  setScaleFactorAction(ctx, closestScaleFactor, 'automatic')
}, 'initializeScaleFactorAction')

const findClosest = (arr: Array<number>, target: number): number => {
  return arr.reduce((prev, curr) =>
    Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev,
  )
}
